import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { fetchActiveFinancingScenarios } from "../services/riskFinancingApi";
import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";
import { probabilityTooltip, lossLabelFormatter} from "../utils/chartFormatters";

import ExecutiveSection from "../components/executive/ExecutiveSection";
import ExecutiveChartCard from "../components/executive/ExecutiveChartCard";
import ExecutiveCard from "../components/executive/ExecutiveCard";
import ExecutiveRecommendationPanel from "../components/executive/ExecutiveRecommendationPanel";
import LoadingOrbit from "../components/LoadingOrbit";
import AnalyticalDecisionJourney from "../components/analytical_journey/AnalyticalDecisionJourney";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LineChart,
  Line,
  ReferenceLine,
} from "recharts";

const sensitivityInputLabels = {
  frequency: "Threat Frequency",
  vulnerability: "Vulnerability",
  loss: "Loss Magnitude",
};

function appliedAssumptionNarrative(appliedInputs, impact, formatMoney) {
  if (!appliedInputs) return null;

  const appliedDrivers = Object.entries(sensitivityInputLabels)
    .filter(([key]) => Number(appliedInputs[key] || 0) !== 0)
    .map(([, label]) => label);

  if (appliedDrivers.length === 0) {
    return "No sensitivity adjustments are currently applied.";
  }

  const direction = Number(impact || 0) >= 0 ? "reduce" : "increase";
  const amount = formatMoney(Math.abs(Number(impact || 0)));

  if (appliedDrivers.length === 1) {
    return `The current sensitivity assumptions ${direction} expected exposure by ${amount}, driven by the applied ${appliedDrivers[0]} adjustment.`;
  }

  const driverList = `${appliedDrivers.slice(0, -1).join(", ")} and ${appliedDrivers.at(-1)}`;
  return `The current sensitivity assumptions ${direction} expected exposure by ${amount} under the applied ${driverList} adjustments.`;
}

export default function SensitivityAnalysis() {
  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const fairParams = useMemo(() => ({
    simulations: Number(settings.simulations || 5000),
    seed: getFairSeedConfig(settings).seed,
  }), [
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);


  const formatMoney = (value, decimals = 0) => {

    if (
      value === null ||
      value === undefined ||
      Number.isNaN(value)
    ) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: settings.currency || "USD",
        maximumFractionDigits: 0,
      }).format(0);
    }

    const number = Number(value);
    const abs = Math.abs(number);
    const sign = number < 0 ? "-" : "";

    const formatter = (amount, digits = decimals) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: settings.currency || "USD",
        maximumFractionDigits: digits,
      }).format(amount);

    if (abs >= 1_000_000_000) {
      return `${sign}${formatter(abs / 1_000_000_000, 1)}B`;
    }

    if (abs >= 1_000_000) {
      return `${sign}${formatter(abs / 1_000_000, 1)}M`;
    }

    if (abs >= 1_000) {
      return `${sign}${formatter(abs / 1_000, 0)}K`;
    }

    return `${sign}${formatter(abs, 0)}`;
  };

  const [data, setData] = useState(null);


  const [scenarioId, setScenarioId] = useState("");
  const [scenarios, setScenarios] = useState([]);

  const [pendingInputs, setPendingInputs] = useState({
    frequency: 0,
    vulnerability: 0,
    loss: 0,
  });
  const [appliedInputs, setAppliedInputs] = useState(null);
  const [requestedInputs, setRequestedInputs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const requestIdRef = useRef(0);

  const hasScenarioSelection = !!scenarioId;
  const [error, setError] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();

        if (!isActive) return;

        setSettings(freshSettings);
      } catch (err) {
        console.error("Error loading platform settings:", err);
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!settingsLoaded) return;
    fetchActiveFinancingScenarios()
      .then((activeScenarios) => {
        setScenarios(activeScenarios);
      })
      .catch((err) =>
        console.error("Error loading active scenarios:", err)
      );
  }, [settingsLoaded]);


  const executeAnalysis = useCallback(async (inputs, selectedScenarioId) => {
      if (!settingsLoaded || !selectedScenarioId) return;

      const requestId = ++requestIdRef.current;
      const requestedInputs = { ...inputs };

      setLoading(true);
      setRequestedInputs(requestedInputs);
      setError(null);

      try {

        const params = {
          scenario_id: selectedScenarioId,
          frequency_delta: requestedInputs.frequency,
          vulnerability_delta: requestedInputs.vulnerability,
          loss_delta: requestedInputs.loss,
          ...fairParams,
        };

        const res = await axios.get(
          "/api/digital-risk/dashboard/fair-sensitivity-simulation/",
          { params }
        );

        if (requestId !== requestIdRef.current) return;
        setData(res.data);
        setAppliedInputs(requestedInputs);

      } catch (err) {
        if (requestId !== requestIdRef.current) return;

        console.error(
          "Error loading sensitivity simulation:",
          err
        );

        setError(
          err?.response?.data?.detail ||
          "Unable to run sensitivity simulation."
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
  }, [fairParams, settingsLoaded]);

  useEffect(() => {
    if (!settingsLoaded || !scenarioId) return;

    const baselineInputs = { frequency: 0, vulnerability: 0, loss: 0 };
    executeAnalysis(baselineInputs, scenarioId);
  }, [executeAnalysis, scenarioId, settingsLoaded]);

  const hasUnappliedChanges = !appliedInputs ||
    pendingInputs.frequency !== appliedInputs.frequency ||
    pendingInputs.vulnerability !== appliedInputs.vulnerability ||
    pendingInputs.loss !== appliedInputs.loss;

  const selectScenario = (nextScenarioId) => {
    requestIdRef.current += 1;
    setScenarioId(nextScenarioId);
    setPendingInputs({ frequency: 0, vulnerability: 0, loss: 0 });
    setAppliedInputs(null);
    setRequestedInputs(null);
    setData(null);
    setError(null);
    setLoading(false);
  };

  const updatePendingInput = (name, value) => {
    setPendingInputs((current) => ({ ...current, [name]: value }));
  };

  return (
    <div className="p-6 space-y-6">

      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
          Strategic Intelligence
        </p>
        <h1 className="text-2xl font-semibold text-slate-900 mt-1">
          Sensitivity Analysis
        </h1>

        <p className="text-sm text-slate-500 mt-1">
            Evaluate how changes in threat frequency, vulnerability and loss factors impact quantified digital risk exposure.
        </p>

        <p className="mt-2 text-xs text-slate-500">
          Monte Carlo: {settings.simulations.toLocaleString()} simulations ·
          Confidence: {settings.confidenceLevel} ·
          Loss model: {settings.lossModel}
        </p>
      </div>

      <AnalyticalDecisionJourney currentStage="understand" />

      <div className="flex justify-end">

        <button
          onClick={() => {
            const nextMode = !presentationMode;
            setPresentationMode(nextMode);

            if (nextMode && !document.fullscreenElement) {
              document.documentElement.requestFullscreen();
            }

            if (!nextMode && document.fullscreenElement) {
              document.exitFullscreen();
            }
          }}
          className={`
            px-4 py-2 rounded-xl text-sm border transition
            ${
              presentationMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }
          `}
        >
          🖥️ {presentationMode ? "Exit Presentation" : "Presentation Mode"}
        </button>
      </div>

      <ExecutiveSection
        title="Sensitivity Simulation Controls"
        subtitle="Simulate how changes in frequency, vulnerability and loss magnitude affect quantified exposure."
      >


        <div className="grid grid-cols-1 md:grid-cols-1 gap-2">

          <div className="mb-6">
            <label htmlFor="sensitivity-scenario" className="block text-sm font-semibold text-slate-700 mb-2">
              Select FAIR Scenario
            </label>

            <select
              id="sensitivity-scenario"
              value={scenarioId}
              onChange={(e) => selectScenario(e.target.value)}
              className="w-full md:w-1/2 border border-slate-300 rounded-xl px-4 py-3 bg-white text-slate-900 shadow-sm"
            >
              <option value="">
                Select a scenario...
              </option>

              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          
        </div>


      </ExecutiveSection>

      {!hasScenarioSelection && (
        <div className="bg-white border border-gray-200 rounded-2xl mt-4 p-6 text-sm text-gray-500">
          Select a FAIR scenario to analyze scenario-level sensitivity.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm">
          {error}
        </div>
      )}

      {hasScenarioSelection && (
      <div className="relative space-y-6">

          {loading && (
            <div className="fixed left-[100px] right-0 bottom-0 top-[260px] z-40 flex items-start justify-center  pt-20">
              <LoadingOrbit
                message=""
                submessage=""
              />
            </div>
          )}

          <div>
            {data?.match && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <div className="text-sm">

                  {data.match.match_type === "scenario_id" ||
                  data.match.match_type === "exact" ? (

                    <span className="text-green-600 font-medium">
                      Scenario model → {data.match.applied.incident_type} |{" "}
                      {data.match.applied.asset_name}
                    </span>

                  ) : (

                    <span className="text-amber-600 font-medium">
                      Using fallback → {data.match.applied.incident_type} |{" "}
                      {data.match.applied.asset_name}
                    </span>

                  )}
                </div>
              </div>
            )}

          </div>

        <>
          <SensitivityCards
            data={data}
            formatMoney={formatMoney}
          />

          {data?.impact && data?.tornado?.top_driver && (
            <>
            <SensitivitySliders
              inputs={pendingInputs}
              onChange={updatePendingInput}
              loading={loading}
              requestedInputs={requestedInputs}
              hasUnappliedChanges={hasUnappliedChanges}
              onRun={() => executeAnalysis(pendingInputs, scenarioId)}
            />
            

            <div
              className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
              data-testid="current-what-if-narrative"
            >
              <p className="text-sm leading-relaxed text-slate-700">
                {appliedAssumptionNarrative(
                  appliedInputs,
                  data.impact.eal_difference,
                  formatMoney
                )}
              </p>
            </div>
            </>
          )}

          <TornadoChart
            data={data}
            loading={loading}
            formatMoney={formatMoney}
          />

          <ExceedanceChart
            data={data}
            formatMoney={formatMoney}
          />

       

        </>
       </div>
      )}



    </div>
  );
}

/* ---------------- cards ---------------- */

function SensitivityCards({ data, formatMoney }) {
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ExecutiveCard
        title="Base EAL"
        value={formatMoney(data.base?.expected_annual_loss)}
      />

      <ExecutiveCard
        title="Adjusted EAL"
        value={formatMoney(data.adjusted?.expected_annual_loss)}
      />

      <ExecutiveCard
        title="Scenario Impact"
        value={formatMoney(data.impact?.eal_difference)}
        accent={
          Number(data.impact?.eal_difference || 0) >= 0
            ? "success"
            : "danger"
        }
      />
    </div>
  );
}
/* ---------------- tornado ---------------- */

function TornadoChart({
  data,
  loading,
  formatMoney,
}) {
  if (!data?.tornado) return null;

  return (
    <ExecutiveChartCard
      title="Tornado Analysis"
      subtitle="Estimated EAL impact from a 20% improvement in each risk driver."
      height={280}
    >

      {data.tornado.top_driver && (
        <div className="mb-4 rounded-xl bg-blue-50 border border-blue-100 p-4">

          <p className="text-sm text-blue-800">
            Biggest driver:{" "}

            <span className="font-semibold">
              {data.tornado.top_driver.driver}
            </span>

            {" "}with estimated impact of{" "}

            <span className="font-semibold">
              {formatMoney(
                data.tornado.top_driver.impact
              )}
            </span>
          </p>
        </div>
      )}

      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
              


        <BarChart
          data={data.tornado.drivers || []}
          layout="vertical"
          margin={{
            top: 10,
            right: 30,
            left: 40,
            bottom: 10,
          }}
        >

          <defs>
            <linearGradient id="tornado-red" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#FCA5A5" />
              <stop offset="100%" stopColor="#DC2626" />
            </linearGradient>

            <linearGradient id="tornado-amber" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>

            <linearGradient id="tornado-green" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#BBF7D0" />
              <stop offset="100%" stopColor="#22C55E" />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          <XAxis
            type="number"
            tickFormatter={(value) =>
              formatMoney(value)
            }
            tick={{ fontSize: 12 }}
          />

          <YAxis
            type="category"
            dataKey="driver"
            width={130}
            tick={{ fontSize: 12 }}
          />

          <Tooltip
            formatter={(value) => [
              formatMoney(value),
              "EAL impact",
            ]}
          />

          <Bar
            dataKey="impact"
            radius={[0, 8, 8, 0]}
          >
            {(data.tornado.drivers || []).map(
              (entry, index) => {

                const max =
                  data.tornado.drivers?.[0]?.impact || 1;

                const ratio =
                  entry.impact / max;

                let color = "url(#tornado-green)";

                if (ratio > 0.7) {
                  color = "url(#tornado-red)";
                } else if (ratio > 0.4) {
                  color = "url(#tornado-amber)";
                }
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={color}
                  />
                );
              }
            )}
          </Bar>
        </BarChart>

        </ResponsiveContainer>
      </div>



    </ExecutiveChartCard>
  );
}

/* ---------------- exceedance ---------------- */

function ExceedanceChart({ data, formatMoney }) {
  if (!data?.exceedance_curve?.length) return null;

  const curvePct = data.exceedance_curve.map((p) => ({
    ...p,
    probability_pct: Number(p.exceedance_probability || 0) * 100,
  }));

  return (
    <ExecutiveChartCard
      title="Loss Exceedance Curve"
      subtitle="Probability of exceeding a given loss level for the selected scenario."
      height={320}
    >
      <LineChart data={curvePct}>
        <CartesianGrid strokeDasharray="3 3" />

        <XAxis
          dataKey="loss"
          type="number"
          domain={[0, "dataMax"]}
          tickFormatter={(value) => formatMoney(value)}
          tick={{ fontSize: 12 }}
        />

        <YAxis
          domain={[0, 100]}
          tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
          tick={{ fontSize: 12 }}
        />

        <Tooltip
          formatter={(value) => [
            `${Number(value).toFixed(2)}%`,
            "Probability",
          ]}
          labelFormatter={(label) => `Loss: ${formatMoney(label)}`}
        />

        <Line
          type="monotone"
          dataKey="probability_pct"
          stroke="#3B82F6"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ExecutiveChartCard>
  );
}

/* ---------------- asset summary ---------------- */

function AssetSensitivitySummary({
  assetSensitivity,
  formatMoney,
}) {
  
  const assets = Array.isArray(assetSensitivity)
    ? assetSensitivity
    : assetSensitivity?.assets || [];

  if (!assets.length) return null;

  return (
    <ExecutiveSection
      title="Asset Sensitivity Summary"
      subtitle="Ranked view of assets and scenarios most sensitive to FAIR risk drivers."
    >
      <div className="space-y-3">
        {assets.map((item, index) => (
          <div
            key={`${item.asset_id}-${index}`}
            className="rounded-xl border border-slate-200 bg-slate-50 p-4"
          >
            <div className="flex items-center justify-between gap-6">
              <div>
                <div className="font-semibold text-slate-900">
                  #{index + 1} {item.asset_name}
                </div>

                <div className="text-sm text-slate-500 mt-1">
                  {item.scenario}
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-500">
                  Top Driver
                </div>

                <div className="font-semibold text-slate-900">
                  {item.top_driver}
                </div>
              </div>

              <div className="text-right">
                <div className="text-xs text-slate-500">
                  Impact
                </div>

                <div className="font-semibold text-slate-900">
                  {formatMoney(item.top_driver_impact)}
                </div>

                <div className="text-xs text-slate-500">
                  {Number(item.top_driver_impact_pct || 0).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ExecutiveSection>
  );
}
/* ---------------- slider ---------------- */


/* ---------------- sliders ---------------- */

function SensitivitySliders({
  inputs,
  onChange,
  loading,
  requestedInputs,
  hasUnappliedChanges,
  onRun,
}) {
  const pendingMatchesActiveRequest = requestedInputs &&
    inputs.frequency === requestedInputs.frequency &&
    inputs.vulnerability === requestedInputs.vulnerability &&
    inputs.loss === requestedInputs.loss;
  const runDisabled = !hasUnappliedChanges || (loading && pendingMatchesActiveRequest);

  return (
    <div className="bg-sky-900 text-white rounded-2xl p-6 shadow-sm">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        <SimulationSlider
          label="Threat Frequency"
          value={inputs.frequency}
          setValue={(value) => onChange("frequency", value)}
          color="blue"
        />

        <SimulationSlider
          label="Vulnerability"
          value={inputs.vulnerability}
          setValue={(value) => onChange("vulnerability", value)}
          color="amber"
        />

        <SimulationSlider
          label="Loss Magnitude"
          value={inputs.loss}
          setValue={(value) => onChange("loss", value)}
          color="red"
        />

      </div>

      <div className="mt-6 flex flex-col items-start gap-2 border-t border-sky-700 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-sky-100" aria-live="polite">
          {hasUnappliedChanges ? "Changes not yet applied" : "Current assumptions applied"}
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={runDisabled}
          className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-sky-900 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading && pendingMatchesActiveRequest ? "Running..." : "Run Analysis"}
        </button>
      </div>

    </div>
  );
}

/* ---------------- cards ---------------- */

function SimulationSlider({
  label,
  value,
  setValue,
}) {

  const isPositive = value > 0;

  return (
    <div>

      <div className="flex items-center justify-between mb-2">

        <div>
          <p className="text-sm text-slate-300">
            {label}
          </p>

          <p className="text-3xl font-bold mt-1">
            {value > 0 ? "+" : ""}
            {value}%
          </p>
        </div>

      </div>

      <input
        aria-label={label}
        type="range"
        min="-50"
        max="50"
        value={value}
        onChange={(e) =>
          setValue(Number(e.target.value))
        }
        className="w-full"
      />

      <div className="flex justify-between text-xs text-slate-400 mt-2">
        <span>-50%</span>
        <span>Baseline</span>
        <span>+50%</span>
      </div>

      <div className="mt-2 text-xs">
        {value === 0 ? (
          <span className="text-slate-400">
            Baseline scenario assumptions
          </span>
        ) : isPositive ? (
          <span className="text-red-300">
            Increased exposure assumptions
          </span>
        ) : (
          <span className="text-green-300">
            Reduced exposure assumptions
          </span>
        )}
      </div>

    </div>
  );
}
